import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useStore, useQuery, useCommand, useRpc } from "../../store";
import { useI18n } from "../../i18n";
import { useStyles, useColors } from "../../ui";
import { PdfForm } from "../../components/PdfForm";
import { FORM_FIELDS } from "./formFields";
import { useAuth } from "../../auth";
import { db } from "../../db";
import { cases as casesTable, events as eventsTable } from "../../../../shared/src/schema";
import { and, eq } from "drizzle-orm";

type ErrorState = "notFound" | "deleted" | "incomplete" | null;

export function CasePage() {
  const { id, eventId } = useParams<{ id: string; eventId?: string }>();
  const { t } = useI18n();
  const { openCase, online } = useStore();
  const dispatch = useCommand();
  const navigate = useNavigate();
  const styles = useStyles();
  const colors = useColors();
  const { user } = useAuth();

  const rpc = useRpc();
  const [errorState, setErrorState] = useState<ErrorState>(null);
  const [deleteModal, setDeleteModal] = useState<{ kind: "blocked"; message: string } | { kind: "confirm" } | null>(null);

  const { data: caseRow, loading: caseRowLoading } = useQuery(
    () => id ? db.select({ createdBy: casesTable.createdBy, archived: casesTable.archived }).from(casesTable).where(eq(casesTable.id, id)).then((r) => r[0] ?? null) : Promise.resolve(null),
    [id],
  );

  const isArchived = caseRow?.archived === 1;

  // medicRole2 sees page 1 only, unless they created the case directly (direct admission)
  const maxPage = user?.role === "medicRole2" && caseRow?.createdBy !== user.userId ? 1 : undefined;

  useEffect(() => {
    if (!id) return;
    setErrorState(null);
    openCase(id).then(async (found: boolean) => {
      if (!found) { setErrorState("notFound"); return; }
      // Server had events but projection may have failed — verify row exists locally
      const row = await db.select({ id: casesTable.id }).from(casesTable).where(eq(casesTable.id, id)).then(r => r[0] ?? null);
      if (!row) {
        const deleted = await db.select({ id: eventsTable.id }).from(eventsTable)
          .where(and(eq(eventsTable.entityId, id), eq(eventsTable.type, "case.deleted")))
          .then(r => r[0] ?? null);
        setErrorState(deleted ? "deleted" : "incomplete");
      }
    }).catch(() => {});
  }, [id, online]);

  if (!id)
    return (
      <div style={{ padding: "12px" }}>
        <p style={{ color: colors.textMuted }}>{t("case.dataMissing")}</p>
      </div>
    );

  const requestDelete = async () => {
    if (!id) return;
    const result = await rpc<{ deletable: boolean; reason?: string }>({ type: "canDelete", entityType: "case", entityId: id });
    if (result.deletable) {
      setDeleteModal({ kind: "confirm" });
    } else {
      setDeleteModal({ kind: "blocked", message: result.reason || t("common.deleteBlocked", { reason: "" }) });
    }
  };

  const confirmDelete = async () => {
    if (!id) return;
    setDeleteModal(null);
    await rpc({ type: "deleteEntity", entityType: "case", entityId: id });
    navigate("/cases");
  };

  const deleteModal_ = (
    <>
      {deleteModal?.kind === "confirm" && (
        <div style={{ marginTop: "16px", padding: "12px", background: colors.bgAlt, borderRadius: "6px" }}>
          <p style={{ color: colors.text, marginBottom: "12px" }}>{t("case.notFound.deleteConfirm")}</p>
          <button style={{ ...styles.btn, background: "#c0392b", color: "#fff", border: "none", marginRight: "8px" }} onClick={confirmDelete}>
            {t("case.notFound.deleteBtn")}
          </button>
          <button style={styles.btn} onClick={() => setDeleteModal(null)}>{t("common.cancel")}</button>
        </div>
      )}
      {deleteModal?.kind === "blocked" && (
        <div style={{ marginTop: "16px", padding: "12px", background: colors.bgAlt, borderRadius: "6px" }}>
          <p style={{ color: colors.textMuted }}>{deleteModal.message}</p>
          <button style={{ ...styles.btn, marginTop: "8px" }} onClick={() => setDeleteModal(null)}>{t("common.cancel")}</button>
        </div>
      )}
    </>
  );

  // Archived — soft tombstone, restorable by admin
  if (!caseRowLoading && isArchived)
    return (
      <div style={{ padding: "24px", maxWidth: "540px", margin: "0 auto" }}>
        <h2 style={{ fontSize: "1.25rem", marginBottom: "12px", color: colors.text }}>
          {t("case.archived.title")}
        </h2>
        <p style={{ color: colors.textMuted, marginBottom: "20px", lineHeight: 1.6 }}>
          {t("case.archived.body")}
        </p>
        <button style={{ ...styles.btn, marginRight: "8px" }} onClick={() => navigate("/cases")}>
          {t("case.notFound.backToCases")}
        </button>
        {user?.role === "admin" && (
          <button
            style={styles.btn}
            onClick={async () => {
              await dispatch("case.unarchive", { uuid: id });
              navigate("/cases", { replace: true });
              setTimeout(() => navigate(`/cases/${id}`), 50);
            }}
          >
            {t("case.archived.unarchiveBtn")}
          </button>
        )}
      </div>
    );

  // Deleted — permanently removed, confirmed by case.deleted events
  if (errorState === "deleted")
    return (
      <div style={{ padding: "24px", maxWidth: "540px", margin: "0 auto" }}>
        <h2 style={{ fontSize: "1.25rem", marginBottom: "12px", color: colors.text }}>
          {t("case.deleted.title")}
        </h2>
        <p style={{ color: colors.textMuted, marginBottom: "20px", lineHeight: 1.6 }}>
          {t("case.deleted.body")}
        </p>
        <button style={styles.btn} onClick={() => navigate(-1)}>
          {t("case.back")}
        </button>
      </div>
    );

  // Incomplete — events exist on server but projection failed, not recoverable
  if (errorState === "incomplete")
    return (
      <div style={{ padding: "24px", maxWidth: "540px", margin: "0 auto" }}>
        <h2 style={{ fontSize: "1.25rem", marginBottom: "12px", color: colors.text }}>
          {t("case.incomplete.title")}
        </h2>
        <p style={{ color: colors.textMuted, marginBottom: "8px", lineHeight: 1.6 }}>
          {t("case.incomplete.body")}
        </p>
        <p style={{ color: colors.textMuted, fontSize: "0.8rem", fontFamily: "monospace", marginBottom: "20px" }}>
          ID: {id}
        </p>
        <button style={{ ...styles.btn, marginRight: "8px" }} onClick={() => navigate(-1)}>
          {t("case.back")}
        </button>
        {user?.role === "admin" && (
          <button
            style={{ ...styles.btn, background: "#c0392b", color: "#fff", border: "none" }}
            onClick={requestDelete}
          >
            {t("case.notFound.deleteBtn")}
          </button>
        )}
        {deleteModal_}
      </div>
    );

  // Not found — never saved to server (ghost case)
  if (errorState === "notFound")
    return (
      <div style={{ padding: "24px", maxWidth: "540px", margin: "0 auto" }}>
        <h2 style={{ fontSize: "1.25rem", marginBottom: "12px", color: colors.text }}>
          {t("case.notFound.title")}
        </h2>
        <p style={{ color: colors.textMuted, marginBottom: "8px", lineHeight: 1.6 }}>
          {t("case.notFound.body")}
        </p>
        <p style={{ color: colors.textMuted, fontSize: "0.8rem", fontFamily: "monospace", marginBottom: "20px" }}>
          ID: {id}
        </p>
        <button style={{ ...styles.btn, marginRight: "8px" }} onClick={() => navigate(-1)}>
          {t("case.back")}
        </button>
        {user?.role === "admin" && (
          <>
            <button
              style={{ ...styles.btn, marginRight: "8px" }}
              onClick={async () => {
                await dispatch("case.archive", { uuid: id });
                navigate("/cases");
              }}
            >
              {t("case.notFound.archiveBtn")}
            </button>
            <button
              style={{ ...styles.btn, background: "#c0392b", color: "#fff", border: "none" }}
              onClick={requestDelete}
            >
              {t("case.notFound.deleteBtn")}
            </button>
          </>
        )}
        {deleteModal_}
      </div>
    );

  return (
    <div style={styles.content}>
      <PdfForm caseId={id} fields={FORM_FIELDS} initialEventId={eventId} maxPage={maxPage} />
    </div>
  );
}
